import React, { useCallback, useEffect, useMemo } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import * as Yup from 'yup';

import { GUID, MembershipType, PersonOrg, PersonOrgType } from '@ndvr/customer-api';

import { FormSheet } from '@/components/FormSheet';
import { getPersonName, getPersonOrgName, useRequiredParams } from 'utils';
import { OrgFormValues } from 'views/person/person-form-model';
import {
    useOrgLinks,
    usePersonOrgProfile,
    usePersonOrgProfilesForHousehold,
    useSaveOrgProfile,
} from 'views/person/person-queries';
import {
    FormInput,
    FormSection,
    FormSelect,
    FormYesNoToggle,
    PageSpinner,
    requiredStringValidator,
    Title,
    VerticalLayout,
} from 'widgets';

type JointMemberFormValues = {
    orgName?: string;
    highNetWorth?: boolean;
    jointOwners?: {
        [MembershipType.JOINT_PRIMARY]: string;
        [MembershipType.JOINT_SECONDARY]: string;
    };
};

const formSchema: Yup.ObjectSchema<JointMemberFormValues> = Yup.object({
    orgName: requiredStringValidator.label('Joint name'),
    highNetWorth: Yup.boolean(),
    jointOwners: Yup.object({
        [MembershipType.JOINT_PRIMARY]: requiredStringValidator.label('Primary owner'),
        [MembershipType.JOINT_SECONDARY]: requiredStringValidator
            .label('Secondary owner')
            .test(
                'secondary-different-from-primary',
                'The secondary owner cannot be the same as the primary owner',
                (jointSecondary, context) => jointSecondary !== context.parent[MembershipType.JOINT_PRIMARY],
            ),
    }).default(undefined),
});

type JointMemberDrawerProps = {
    onClose: () => void;
    orgGUID?: GUID<PersonOrg>;
};

export const JointMemberDrawer = ({ onClose, orgGUID }: JointMemberDrawerProps) => {
    const { globalAccountGUID } = useRequiredParams(['globalAccountGUID']);
    const isEditing = !!orgGUID;
    const { data: orgProfile, isPending: orgProfileLoading } = usePersonOrgProfile(orgGUID);
    const { data: orgLinks, isPending: orgLinksLoading } = useOrgLinks(orgGUID);
    const { mutate: saveOrgProfile, isPending: saveInProgress } = useSaveOrgProfile({
        globalAccountGUID,
        orgGUID,
    });

    const defaultValues = useMemo<Partial<JointMemberFormValues>>(() => {
        if (!orgProfile) {
            return {};
        }

        const primary = orgLinks?.find((link) => link.membershipType === MembershipType.JOINT_PRIMARY);
        const secondary = orgLinks?.find((link) => link.membershipType === MembershipType.JOINT_SECONDARY);

        return {
            orgName: orgProfile.personOrg.orgName?.plainText,
            highNetWorth: orgProfile.financialInformation?.highNetWorth,
            jointOwners:
                primary?.memberPersonOrgGUID && secondary?.memberPersonOrgGUID
                    ? {
                          [MembershipType.JOINT_PRIMARY]: primary.memberPersonOrgGUID,
                          [MembershipType.JOINT_SECONDARY]: secondary.memberPersonOrgGUID,
                      }
                    : undefined,
        };
    }, [orgLinks, orgProfile]);

    const onSubmit = useCallback(
        (data: JointMemberFormValues, close: () => void) => {
            const primary = data.jointOwners?.[MembershipType.JOINT_PRIMARY];
            const secondary = data.jointOwners?.[MembershipType.JOINT_SECONDARY];

            if (!primary || !secondary) {
                return;
            }

            const payload: OrgFormValues = {
                personOrgType: PersonOrgType.JOINT_PERSONS,
                orgName: data.orgName,
                highNetWorth: data.highNetWorth,
                members: {
                    [MembershipType.JOINT_PRIMARY]: [
                        {
                            parentPersonOrgGUID: '',
                            memberPersonOrgGUID: primary,
                            membershipType: MembershipType.JOINT_PRIMARY,
                        },
                    ],
                    [MembershipType.JOINT_SECONDARY]: [
                        {
                            parentPersonOrgGUID: '',
                            memberPersonOrgGUID: secondary,
                            membershipType: MembershipType.JOINT_SECONDARY,
                        },
                    ],
                },
            };

            saveOrgProfile(payload, { onSuccess: close });
        },
        [saveOrgProfile],
    );

    return (
        <FormSheet
            size="lg"
            title={isEditing ? `Edit ${getPersonOrgName(orgProfile?.personOrg)}` : 'New Joint'}
            onClose={onClose}
            onSubmit={onSubmit}
            formSchema={formSchema}
            defaultValues={defaultValues}
            isLoading={isEditing && (orgProfileLoading || orgLinksLoading)}
            pending={saveInProgress}
            submitLabel={isEditing ? 'Save' : 'Create'}
        >
            <JointMemberFormFields isEditing={isEditing} />
        </FormSheet>
    );
};

const JointMemberFormFields = ({ isEditing }: { isEditing: boolean }) => {
    const { globalAccountGUID } = useRequiredParams(['globalAccountGUID']);
    const { data: personOrgs, isLoading } = usePersonOrgProfilesForHousehold(globalAccountGUID);
    const {
        setValue,
        formState: { dirtyFields },
    } = useFormContext<JointMemberFormValues>();
    const jointOwners = useWatch({ name: 'jointOwners' });

    useEffect(() => {
        const primaryOwner = personOrgs?.find(
            (personOrgProfile) =>
                personOrgProfile.personOrg.personOrgGUID === jointOwners?.[MembershipType.JOINT_PRIMARY],
        );
        const secondaryOwner = personOrgs?.find(
            (personOrgProfile) =>
                personOrgProfile.personOrg.personOrgGUID === jointOwners?.[MembershipType.JOINT_SECONDARY],
        );

        if (isEditing || !primaryOwner || !secondaryOwner || dirtyFields?.orgName) {
            return;
        }

        setValue(
            'orgName',
            `${getPersonName(primaryOwner?.personOrg)} & ${getPersonName(secondaryOwner?.personOrg)} joint`,
            { shouldValidate: true, shouldDirty: false },
        );
    }, [dirtyFields?.orgName, isEditing, jointOwners, personOrgs, setValue]);

    const availablePersons = useMemo(
        () =>
            (personOrgs ?? []).filter(
                (personOrgProfile) => personOrgProfile.personOrg.personOrgType === PersonOrgType.PERSON,
            ),
        [personOrgs],
    );

    if (isLoading) {
        return <PageSpinner />;
    }

    return (
        <VerticalLayout $gap={24}>
            <FormSection title="Joint Details" hasDivider>
                <FormInput
                    name="orgName"
                    placeholder="Name"
                    label="Joint name"
                    description="Auto-fill based on selected Owners"
                />
                <FormYesNoToggle name="highNetWorth" label="High Net Worth" />
            </FormSection>
            <FormSection hasDivider>
                <Title content>Joint Owners</Title>
                <VerticalLayout $gap={16}>
                    <FormSelect name={`jointOwners.${MembershipType.JOINT_PRIMARY}`} label="Primary">
                        <option></option>
                        {availablePersons.map(({ personOrg }) => (
                            <option key={personOrg.personOrgGUID} value={personOrg.personOrgGUID}>
                                {getPersonName(personOrg)}
                            </option>
                        ))}
                    </FormSelect>
                    <FormSelect name={`jointOwners.${MembershipType.JOINT_SECONDARY}`} label="Secondary">
                        <option></option>
                        {availablePersons.map(({ personOrg }) => (
                            <option key={personOrg.personOrgGUID} value={personOrg.personOrgGUID}>
                                {getPersonName(personOrg)}
                            </option>
                        ))}
                    </FormSelect>
                </VerticalLayout>
            </FormSection>
        </VerticalLayout>
    );
};
